import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button, Form } from "react-bootstrap";
import { FiRefreshCcw, FiChevronDown, FiX, FiSearch } from "react-icons/fi";

const CustomAsyncDropdown = ({
  options = [],
  value = [],
  onChange,
  placeholder = "Select options...",
  onRefresh,
  onInputChange,
  onMenuScrollToBottom,
  onMenuOpen,
  isLoading = false,
  isSearchable = true,
  isMulti = true,
  isDisabled = false,
  showSelectAll = false,
  renderOption,
  menuHeader,
  menuFooter,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [selectedValues, setSelectedValues] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const searchInputRef = useRef(null);
  const menuRef = useRef(null);
  const debounceTimeoutRef = useRef(null);
  const selectedContainerRef = useRef(null);

  useEffect(() => {
    if (Array.isArray(value)) {
      setSelectedValues(value);
    } else if (value && typeof value === 'object') {
      setSelectedValues([value]);
    } else {
      setSelectedValues([]);
    }
  }, [value]);

  // Filter options based on search term
  useEffect(() => {
    const trimmed = searchTerm.trim();
    if (trimmed === "") {
      setFilteredOptions(options);
    } else {
      const lower = trimmed.toLowerCase();
      const filtered = options.filter((option) =>
        option.label.toLowerCase().includes(lower),
      );
      setFilteredOptions(filtered);
    }
    setHighlightedIndex(-1);
  }, [searchTerm, options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target) &&
        document.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle search input with debounce
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      if (onInputChange && typeof onInputChange === "function") {
        onInputChange(val.trim());
      }
    }, 800);
  };

  // Handle option selection
  const handleSelectOption = (option) => {
    let newSelectedValues;

    if (isMulti) {
      const isAlreadySelected = selectedValues.some(
        (item) => item.value === option.value,
      );

      if (isAlreadySelected) {
        newSelectedValues = selectedValues.filter(
          (item) => item.value !== option.value,
        );
      } else {
        newSelectedValues = [...selectedValues, option];
      }
    } else {
      newSelectedValues = [option];
      setIsOpen(false);
    }

    setSelectedValues(newSelectedValues);

    if (onChange) {
      onChange(isMulti ? newSelectedValues : newSelectedValues[0]);
    }

    if (!isMulti) {
      setSearchTerm("");
    }
  };

  // Remove selected option
  const handleRemoveOption = (optionValue, e) => {
    e.stopPropagation();
    if (isDisabled) return;
    const newSelectedValues = selectedValues.filter(
      (item) => item.value !== optionValue,
    );

    setSelectedValues(newSelectedValues);

    if (onChange) {
      onChange(isMulti ? newSelectedValues : null);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : prev,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelectOption(filteredOptions[highlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  // Handle scroll for infinite loading
  const handleMenuScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      if (onMenuScrollToBottom && !isLoading) {
        onMenuScrollToBottom();
      }
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    setSearchTerm("");
    if (onRefresh) {
      onRefresh();
    }
  };

  // Toggle dropdown
  const toggleDropdown = () => {
    if (isDisabled) return;
    const nextOpen = !isOpen;
    setIsOpen(nextOpen);
    if (nextOpen) {
      if (onMenuOpen) onMenuOpen();
      if (searchInputRef.current) {
        setTimeout(() => searchInputRef.current?.focus(), 0);
      }
    }
  };

  return (
    <div className="position-relative" ref={wrapperRef}>
      {/* Main dropdown container */}
      <div
        className={`form-control p-0 ${isDisabled ? 'bg-light text-muted' : ''}`}
        style={{
          minHeight: "42px",
          cursor: isDisabled ? "not-allowed" : "pointer",
          backgroundColor: isDisabled ? "#e9ecef" : "white",
          borderColor: isOpen ? "#ea580c" : "#dee2e6",
          boxShadow: isOpen ? "0 0 0 0.2rem rgba(234, 88, 12, 0.25)" : "none",
          ...rest.style,
        }}
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        {/* Container for selected items with horizontal scroll */}
        <div
          ref={selectedContainerRef}
          className="d-flex align-items-center h-100"
          style={{
            padding: "6px 12px",
            minHeight: "40px",
            gap: "4px",
            overflowX: "auto",
            overflowY: "hidden",
            whiteSpace: "nowrap",
            scrollbarWidth: "thin",
            msOverflowStyle: "none",
          }}
        >
          {/* Hide scrollbar for Chrome/Safari */}
          <style>
            {`
              div::-webkit-scrollbar {
                height: 4px;
              }
              div::-webkit-scrollbar-track {
                background: #f1f1f1;
                border-radius: 2px;
              }
              div::-webkit-scrollbar-thumb {
                background: #ccc;
                border-radius: 2px;
              }
              div::-webkit-scrollbar-thumb:hover {
                background: #aaa;
              }
            `}
          </style>

          {selectedValues.length > 0 ? (
            <>
              {selectedValues.map((item) => {
                if (!item || !item.label) return null;

                return (
                  <div
                    key={item.value}
                    className="d-inline-flex align-items-center"
                    style={{
                      flexShrink: 0,
                      padding: "2px 8px",
                      backgroundColor: "#fff7ed",
                      border: "1px solid #fed7aa",
                      borderRadius: "4px",
                      fontSize: "0.8125rem",
                      color: "#9a3412",
                      marginRight: "4px",
                    }}
                  >
                    <span
                      style={{
                        maxWidth: "180px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.label}
                    </span>
                    <FiX
                      size={12}
                      className="ms-1 cursor-pointer"
                      onClick={(e) => handleRemoveOption(item.value, e)}
                      style={{
                        color: "#9a3412",
                        flexShrink: 0,
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.color = "#ea580c")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.color = "#9a3412")
                      }
                    />
                  </div>
                );
              })}
              <div style={{ minWidth: "24px", flexShrink: 0 }}></div>
            </>
          ) : (
            <span className="text-muted" style={{ fontSize: "0.875rem" }}>
              {placeholder}
            </span>
          )}

          {/* Dropdown icon - positioned absolutely to right */}
          <div
            className="d-flex align-items-center"
            style={{
              position: "absolute",
              right: "8px",
              top: "0",
              bottom: "0",
              backgroundColor: "transparent",
              paddingLeft: "8px",
            }}
          >
            {isLoading && (
              <div
                className="spinner-border spinner-border-sm me-2 text-primary"
                role="status"
                style={{ color: "#ea580c" }}
              >
                <span className="visually-hidden">Loading...</span>
              </div>
            )}
            <FiChevronDown
              className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
              style={{
                transition: "transform 0.2s",
                color: "#6c757d",
                transform: isOpen ? "rotate(180deg)" : "rotate(0deg)"
              }}
            />
          </div>
        </div>
      </div>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          ref={menuRef}
          className="position-absolute bg-white border rounded mt-1 w-100"
          onClick={(e) => e.stopPropagation()}
          style={{
            zIndex: 1060,
            maxHeight: "300px",
            overflow: "hidden",
            borderColor: "#dee2e6",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            top: "100%",
          }}
        >
          {/* Search input */}
          {isSearchable && (
            <div
              className="p-2 border-bottom"
              style={{ borderColor: "#dee2e6" }}
            >
              <div className="input-group input-group-sm">
                <span
                  className="input-group-text bg-white"
                  style={{
                    borderColor: "#dee2e6",
                    borderRight: "none",
                  }}
                >
                  <FiSearch size={14} color="#6c757d" />
                </span>
                <input
                  ref={searchInputRef}
                  type="text"
                  className="form-control"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                  style={{
                    borderColor: "#dee2e6",
                    borderLeft: "none",
                    fontSize: "0.875rem",
                    boxShadow: "none"
                  }}
                />
                {onRefresh && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleRefresh}
                    title="Refresh"
                    style={{
                      borderColor: "#dee2e6",
                      color: "#6c757d",
                    }}
                  >
                    <FiRefreshCcw size={14} />
                  </Button>
                )}
              </div>
            </div>
          )}

          {menuHeader && (
            <div className="border-bottom bg-white">
              {menuHeader}
            </div>
          )}

          {/* Options list */}
          <div
            className="overflow-auto"
            style={{ maxHeight: "250px" }}
            onScroll={handleMenuScroll}
          >
            {isMulti && showSelectAll && filteredOptions.length > 0 && (
              <div
                className="p-2 d-flex align-items-center border-bottom bg-light"
                onClick={(e) => {
                  e.stopPropagation();
                  const allSelected = filteredOptions.every(opt => 
                    selectedValues.some(sv => sv.value === opt.value)
                  );
                  
                  let newSelectedValues;
                  if (allSelected) {
                    // Deselect all filtered options
                    newSelectedValues = selectedValues.filter(sv => 
                      !filteredOptions.some(opt => opt.value === sv.value)
                    );
                  } else {
                    // Select all filtered options
                    const toAdd = filteredOptions.filter(opt => 
                      !selectedValues.some(sv => sv.value === opt.value)
                    );
                    newSelectedValues = [...selectedValues, ...toAdd];
                  }
                  
                  setSelectedValues(newSelectedValues);
                  if (onChange) {
                    onChange(newSelectedValues);
                  }
                }}
                style={{
                  cursor: "pointer",
                  minHeight: "36px",
                  fontSize: "0.875rem",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                }}
              >
                <div
                  className="me-2 d-flex align-items-center justify-content-center"
                  style={{
                    width: "16px",
                    height: "16px",
                    border: filteredOptions.every(opt => selectedValues.some(sv => sv.value === opt.value)) ? "none" : "2px solid #dee2e6",
                    borderRadius: "3px",
                    backgroundColor: filteredOptions.every(opt => selectedValues.some(sv => sv.value === opt.value)) ? "#ea580c" : "white",
                    flexShrink: 0,
                  }}
                >
                  {filteredOptions.every(opt => selectedValues.some(sv => sv.value === opt.value)) && (
                    <div
                      style={{
                        width: "6px",
                        height: "6px",
                        backgroundColor: "white",
                        borderRadius: "1px",
                      }}
                    />
                  )}
                </div>
                <span style={{ fontWeight: "600", color: "#ea580c" }}>
                  Select All {searchTerm ? `"${searchTerm}"` : ""}
                </span>
              </div>
            )}
            {filteredOptions.length === 0 ? (
              <div
                className="p-3 text-center"
                style={{ color: "#6c757d", fontSize: "0.875rem" }}
              >
                {isLoading ? "Loading..." : "No options found"}
              </div>
            ) : (
              filteredOptions.map((option, index) => {
                const isSelected = selectedValues.some(
                  (item) => item.value === option.value,
                );
                const isHighlighted = index === highlightedIndex;

                return (
                  <div
                    key={option.value}
                    className={`p-2 d-flex align-items-center`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectOption(option);
                    }}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    style={{
                      cursor: "pointer",
                      borderBottom: "1px solid #f8f9fa",
                      backgroundColor: isHighlighted ? "#fff7ed" : "white",
                      color: "#1a1a1a",
                      minHeight: "36px",
                      fontSize: "0.875rem",
                    }}
                  >
                    <div
                      className="d-flex align-items-center"
                      style={{ width: "100%" }}
                    >
                      {isMulti && (
                        <div
                          className="me-2 d-flex align-items-center justify-content-center"
                          style={{
                            width: "16px",
                            height: "16px",
                            border: isSelected ? "none" : "2px solid #dee2e6",
                            borderRadius: "3px",
                            backgroundColor: isSelected ? "#ea580c" : "white",
                            flexShrink: 0,
                          }}
                        >
                          {isSelected && (
                            <div
                              style={{
                                width: "6px",
                                height: "6px",
                                backgroundColor: "white",
                                borderRadius: "1px",
                              }}
                            />
                          )}
                        </div>
                      )}

                      <span
                        style={{
                          fontWeight: isSelected ? "600" : "400",
                          color: isSelected ? "#ea580c" : "#1a1a1a",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {renderOption ? renderOption(option) : option.label}
                      </span>
                    </div>
                  </div>
                );
              })
            )}

            {isLoading && filteredOptions.length > 0 && (
              <div className="p-2 text-center">
                <div
                  className="spinner-border spinner-border-sm"
                  role="status"
                  style={{ color: "#ea580c" }}
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}
            
            {menuFooter && (
              <div className="border-top bg-light">
                {menuFooter}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomAsyncDropdown;
